import { notification } from "antd";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { AnalysisSource } from "../Enums/AnalysisSource";
import { AnalysisStatus } from "../Enums/AnalysisStatus";
import { DEFAULT_NOTIFICATION_DURATION } from "../Logic/NotificationLogic";
import { IProcessReportsResult } from "../Models/IProcessReportsResult";

import { IAnalysisItem, IBatch, IFile, IRootReducer } from "../Models/Models";
import { getUniqueReportTypes } from "../Reducers/ReportsReducer";
import { mapBatchesToAnalysisItems } from "../Util/ReportUtil";
import * as types from "./Types";

export const loadReports: ActionCreator<ThunkAction<any, any, any, any>> = (
    isCollaborating: boolean,
    startDate: Date,
    endDate: Date
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingReports(true));
        dispatch(setReportsCollaborating(isCollaborating));
        (window as any).scout?.interop.api.reports.setReportsCollaborating(
            isCollaborating
        );
        dispatch(setReportsDates(startDate, endDate));

        axios.post("/Batches/History", {
            isCollaborating: isCollaborating,
            startDate: startDate,
            endDate: endDate,
        }).then((response) => {
            const batches: IBatch[] = response.data.data;

            if (batches && batches.length > 0) {
                dispatch(setFoundReports(true));
            } else {
                dispatch(setFoundReports(false));
            }
            dispatch(setLoadingReports(false));
            dispatch(processReports(batches));
        }).catch((error) => {
            dispatch(setLoadingReports(false));
        });
    };
};

export const setReportIdAndFiles: ActionCreator<
    ThunkAction<any, any, any, any>
> = (batchId: number) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setSelectedReportId(batchId));
        dispatch(setSelectedReportFiles([]));

        axios
            .get("/Batches/Files", {
                params: {
                    batchId: batchId,
                    collaborateWithCompany: true,
                },
            })
            .then((response) => {
                const files: IFile[] = response.data.data;
                dispatch(setSelectedReportFiles(files));
            })
            .catch((error) => {
                console.error("Report Actions: setReportIdAndFiles()", error);
                dispatch(setSelectedReportFiles(null));
            });
    };
};

export const viewKMLFileOnMap = (file: IFile, path: string): void => {
    (window as any).scout.actions.kmlFileToggle(file, path);
};

export const deleteReport: ActionCreator<ThunkAction<any, any, any, any>> = (report: IBatch) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingReports(true));

        axios.post("/Batches/DeleteBatch", report).then((response: AxiosResponse) => {
            if (response.data.result) {
                const batchID: number = response.data.data;
                notification.success({
                    message: "Report Deleted",
                    duration: DEFAULT_NOTIFICATION_DURATION, // seconds
                });
                dispatch(deleteReportSuccess(batchID));
                dispatch(setLoadingReports(false));
            } else {
                console.log(
                    "server call succeeded, but the operation failed: ",
                    response
                );
                notification.error({
                    message:
                        "Failed to delete report. If you continue to see this message, please contact SCOUT Support.",
                    duration: null,
                });
                dispatch(setLoadingReports(false));
            }
        }).catch((error: AxiosError) => {
            console.log("delete error: ", error);
            dispatch(setLoadingReports(false));
        });
    };
};

export const resetReportCounter = () => ({
    type: types.RESET_REPORT_COUNTER,
});

const processReports = (reports: IBatch[]) => {
    return (dispatch: Dispatch<any>, getState) => {
        const state = (getState() as IRootReducer).reports;
        /** copy of current reports (for notification purposes) */
        const previousReports: IAnalysisItem[] = [...state.allReports];
        /** map incoming data to object */
        const incomingReports: IAnalysisItem[] = mapBatchesToAnalysisItems(
            reports
        );
        let unviewedCompletedReportsCount = state.unviewedReportCount;

        for (const incomingReport of incomingReports) {
            const previousReport = previousReports.find(p => p.id === incomingReport.id);
            if (previousReport) {
                /** check status changes */
                if (
                    incomingReport.status === AnalysisStatus.Complete
                    && previousReport.status !== AnalysisStatus.Complete
                ) {
                    unviewedCompletedReportsCount++;
                } else if (
                    incomingReport.status === AnalysisStatus.CompleteWithErrors
                    && previousReport.status !== AnalysisStatus.CompleteWithErrors
                ) {
                    notification.error({
                        message: `${incomingReport.name} had an error. If you continue to experience problems, please contact SCOUT Support.`,
                        duration: null, // user must close
                    });
                }
                break;
            }
        }

        /** remove all previous reports of this type */
        const reportsCopy: IAnalysisItem[] = [...state.allReports].filter(
            (report: IAnalysisItem) => {
                return report.source !== AnalysisSource.Nexus;
            }
        );
        /** add the new reports from the server */
        reportsCopy.push(...incomingReports);

        const result: IProcessReportsResult = {
            allReports: reportsCopy,
            // if there weren't previous reports, don't trust the unviewed count
            unviewedReportCount:
                previousReports.filter(r => r.source === AnalysisSource.Nexus).length
                    ? unviewedCompletedReportsCount
                    : state.unviewedReportCount,
            uniqueReportTypes: getUniqueReportTypes(reportsCopy),
        };

        dispatch({
            type: types.SET_REPORTS,
            data: result
        });
    };
};

export const setLoadingReports = (loading: boolean) => ({
    type: types.SET_LOADING_REPORTS,
    data: loading,
});

const setFoundReports = (found: boolean) => ({
    type: types.SET_FOUND_REPORTS,
    data: found,
});

const setSelectedReportId = (selectedReportId: number) => ({
    type: types.SET_SELECTED_REPORT_ID,
    data: selectedReportId,
});

const setSelectedReportFiles = (files: IFile[]) => ({
    type: types.SET_SELECTED_REPORT_FILES,
    data: files,
});

const deleteReportSuccess = (deletedBatchID: number) => ({
    type: types.DELETE_REPORT_SUCCESS,
    data: deletedBatchID,
});

export const setReportsDates = (startDate: Date, endDate: Date) => ({
    type: types.SET_REPORTS_DATES,
    data: { startDate, endDate },
});

export const setInsightsCollaborating = (collaborating: boolean) => ({
    type: types.SET_INSIGHTS_COLLABORATING,
    data: collaborating,
});

export const setReportsCollaborating = (collaborating: boolean) => ({
    type: types.SET_REPORTS_COLLABORATING,
    data: collaborating,
});

export const setSelectedFile = (selectedFile: IFile) => ({
    type: types.SET_REPORT_FILE,
    data: selectedFile,
});

export const setShowReportFileModal = (showReportFileModal: boolean) => ({
    type: types.SET_SHOW_REPORT_FILE_MODAL,
    data: showReportFileModal,
});

export const setShowRecentReportsModal = (showRecentReportsModal: boolean) => ({
    type: types.SET_SHOW_RECENT_REPORTS_MODAL,
    data: showRecentReportsModal,
});
